import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import { fetchBudgetCycles, fetchBudgetPeriods } from '../forms/forms.apis';
import {
  createActivityPlan,
  fetchActivitiesList,
  fetchActivityPlans,
  updateActivityPlan,
} from './activity.apis';
import {
  IActivityForm,
  IActivityListItem,
  IActivityPlanForm,
  IActivityPlanItem,
} from './activity.models';
import { ILookupItem } from '../forms/forms.models';

type Props = {
  navigate?: (path: string) => void;
  activityix?: string;
};

type State = {
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  activities: IActivityListItem[];
  periods: ILookupItem[];
  cycles: ILookupItem[];

  selectedActivityix: string;
  plans: IActivityPlanItem[];

  dialogOpen: boolean;
  dialogMode: 'add' | 'edit';
  form: IActivityPlanForm;
};

export default class ActivityPlanPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,
      error: '',
      success: '',

      activities: [],
      periods: [],
      cycles: [],

      selectedActivityix: props.activityix || '',
      plans: [],

      dialogOpen: false,
      dialogMode: 'add',
      form: this.getEmptyForm(props.activityix || ''),
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private getEmptyForm = (activityix: string): IActivityPlanForm => {
    return {
      activityix,
      periodix: '',
      cycleix: '',
      drivervalue: '',
      targetvalue: '',
      plannedamount: '',
      description: '',
      status: 1,
    };
  };

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activitiesResult, periods, cycles] = await Promise.all([
        fetchActivitiesList({
          search: '',
          typeix: '',
          costcenterix: '',
          status: '1',
          page: 1,
          pagesize: 1000,
        }),
        fetchBudgetPeriods(),
        fetchBudgetCycles(),
      ]);

      const activities = activitiesResult.items || [];
      const selectedActivityix =
        this.state.selectedActivityix || activities[0]?.recix || '';

      this.setState(
        {
          activities,
          periods,
          cycles,
          selectedActivityix,
          form: this.getEmptyForm(selectedActivityix),
        },
        async () => {
          if (selectedActivityix) {
            await this.loadPlans(selectedActivityix);
          } else {
            this.setState({ loading: false });
          }
        }
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity planning page',
      });
    }
  };

  private loadPlans = async (activityix: string): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
      success: '',
    });

    try {
      const result = await fetchActivityPlans(activityix);

      this.setState({
        plans: result.items || [],
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity plans',
      });
    }
  };

  private handleActivityChange = (event: SelectChangeEvent<string>): void => {
    const selectedActivityix = event.target.value;

    this.setState(
      {
        selectedActivityix,
        form: this.getEmptyForm(selectedActivityix),
      },
      () => {
        if (selectedActivityix) {
          this.loadPlans(selectedActivityix);
        }
      }
    );
  };

  private openAddDialog = (): void => {
    this.setState({
      dialogOpen: true,
      dialogMode: 'add',
      form: this.getEmptyForm(this.state.selectedActivityix),
      error: '',
      success: '',
    });
  };

  private openEditDialog = (plan: IActivityPlanItem): void => {
    this.setState({
      dialogOpen: true,
      dialogMode: 'edit',
      form: {
        recix: plan.recix,
        activityix: this.state.selectedActivityix,
        periodix: plan.period?.recix || '',
        cycleix: plan.cycle?.recix || '',
        drivervalue: plan.drivervalue ?? '',
        targetvalue: plan.targetvalue ?? '',
        plannedamount: plan.plannedamount ?? '',
        description: plan.description || '',
        status: plan.status ?? 1,
      },
      error: '',
      success: '',
    });
  };

  private closeDialog = (): void => {
    this.setState({
      dialogOpen: false,
      form: this.getEmptyForm(this.state.selectedActivityix),
    });
  };

  private handleTextChange =
    (field: keyof IActivityPlanForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private handleNumberChange =
    (field: keyof IActivityPlanForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const rawValue = event.target.value;
      const value = rawValue === '' ? '' : Number(rawValue);

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: value,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IActivityPlanForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          [field]: field === 'status' ? Number(value) : value,
        },
      }));
    };

  private validateForm = (): string[] => {
    const { form } = this.state;
    const errors: string[] = [];

    if (!form.activityix) errors.push('Activity is required.');
    if (!form.periodix) errors.push('Budget period is required.');
    if (!form.cycleix) errors.push('Budget cycle is required.');

    if (form.drivervalue === '' || Number(form.drivervalue) < 0) {
      errors.push('Driver value is required.');
    }

    if (form.targetvalue === '' || Number(form.targetvalue) < 0) {
      errors.push('Target value is required.');
    }

    if (form.plannedamount === '' || Number(form.plannedamount) < 0) {
      errors.push('Planned amount is required.');
    }

    return errors;
  };

  private handleSave = async (): Promise<void> => {
    const errors = this.validateForm();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      if (this.state.dialogMode === 'add') {
        await createActivityPlan(this.state.form);
      } else if (this.state.form.recix) {
        await updateActivityPlan(this.state.form.recix, this.state.form);
      }

      this.setState({
        saving: false,
        dialogOpen: false,
        success: 'Activity plan saved successfully.',
        form: this.getEmptyForm(this.state.selectedActivityix),
      });

      if (this.state.selectedActivityix) {
        await this.loadPlans(this.state.selectedActivityix);
      }
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save activity plan',
      });
    }
  };

  private handleBack = (): void => {
    const path = '/forms/activities';

    if (this.props.navigate) {
      this.props.navigate(path);
      return;
    }

    window.location.href = path;
  };

  private handleRefresh = (): void => {
    if (this.state.selectedActivityix) {
      this.loadPlans(this.state.selectedActivityix);
    }
  };

  render(): React.ReactNode {
    const {
      loading,
      saving,
      error,
      success,
      activities,
      periods,
      cycles,
      selectedActivityix,
      plans,
      dialogOpen,
      dialogMode,
      form,
    } = this.state;

    const selectedActivity =
      activities.find((x) => x.recix === selectedActivityix) || null;

    const totalPlannedAmount = plans.reduce(
      (sum, item) => sum + Number(item.plannedamount || 0),
      0
    );

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Activity Planning
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage planned driver values, targets, and planned amounts for ABB activities.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={this.handleRefresh}
              disabled={!selectedActivityix}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.openAddDialog}
              disabled={!selectedActivityix}
            >
              Add Plan
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-plan-activity-label">
                    Activity
                  </InputLabel>
                  <Select
                    labelId="activity-plan-activity-label"
                    value={selectedActivityix}
                    label="Activity"
                    onChange={this.handleActivityChange}
                  >
                    {activities.map((activity) => (
                      <MenuItem key={activity.recix} value={activity.recix}>
                        {activity.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Selected:</strong> {selectedActivity?.recname || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Code:</strong> {selectedActivity?.shortcode || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Planned Amount:</strong>{' '}
                    {Number(totalPlannedAmount || 0).toLocaleString()}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Stack spacing={2}>
          {loading ? (
            <Card>
              <CardContent>
                <Typography>Loading activity plans...</Typography>
              </CardContent>
            </Card>
          ) : plans.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  No plans defined for this activity yet.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            plans.map((plan, index) => (
              <Card key={plan.recix || index}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {plan.period?.recname || '-'} / {plan.cycle?.recname || '-'}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          label={`Driver: ${plan.drivervalue}`}
                        />
                        <Chip
                          size="small"
                          label={`Target: ${plan.targetvalue}`}
                        />
                        <Chip
                          size="small"
                          label={`Amount: ${Number(plan.plannedamount || 0).toLocaleString()}`}
                        />
                        <Chip
                          size="small"
                          color={plan.status === 1 ? 'success' : 'default'}
                          label={plan.status === 1 ? 'Active' : 'Disabled'}
                        />
                      </Stack>

                      <Typography variant="body2">
                        <strong>Description:</strong> {plan.description || '-'}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => this.openEditDialog(plan)}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        <Dialog
          open={dialogOpen}
          onClose={this.closeDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === 'add' ? 'Add Activity Plan' : 'Edit Activity Plan'}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="plan-period-label">Budget Period</InputLabel>
                  <Select
                    labelId="plan-period-label"
                    value={form.periodix}
                    label="Budget Period"
                    onChange={this.handleSelectChange('periodix')}
                  >
                    {periods.map((period) => (
                      <MenuItem key={period.recix} value={period.recix}>
                        {period.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="plan-cycle-label">Budget Cycle</InputLabel>
                  <Select
                    labelId="plan-cycle-label"
                    value={form.cycleix}
                    label="Budget Cycle"
                    onChange={this.handleSelectChange('cycleix')}
                  >
                    {cycles.map((cycle) => (
                      <MenuItem key={cycle.recix} value={cycle.recix}>
                        {cycle.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Driver Value"
                  type="number"
                  value={form.drivervalue}
                  onChange={this.handleNumberChange('drivervalue')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Target Value"
                  type="number"
                  value={form.targetvalue}
                  onChange={this.handleNumberChange('targetvalue')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Planned Amount"
                  type="number"
                  value={form.plannedamount}
                  onChange={this.handleNumberChange('plannedamount')}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="plan-status-label">Status</InputLabel>
                  <Select
                    labelId="plan-status-label"
                    value={`${form.status}`}
                    label="Status"
                    onChange={this.handleSelectChange('status')}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  minRows={3}
                  value={form.description}
                  onChange={this.handleTextChange('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={this.closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={this.handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}